import RecruitmentAsset from "./RecruitmentAsset";
import "./RecruitmentForms.css";
import techStackIcon from "../assets/CreateJobAssest/tech-stack-icon.svg";
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  Paper,
  Grid,
  Chip,
  LinearProgress,
  Alert,
  Card,
  CardContent,
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';

const TechStackModal = ({
  open,
  onClose,
  techs,
  difficulties,
  resumeEvaluationPercentage = 0,
  onSave,
  resumeEvaluation,
}) => {
  const [localTechs, setLocalTechs] = useState([]);
  const [resumeEvalPercent, setResumeEvalPercent] = useState(resumeEvaluationPercentage);
  const [newTechName, setNewTechName] = useState('');
  const [newTechPercentage, setNewTechPercentage] = useState('');
  const [newTechDifficulty, setNewTechDifficulty] = useState('Intermediate');

  const clampAllocation = (value, maximum = 100) =>
    Math.min(Math.max(0, Number.parseInt(value, 10) || 0), Math.max(0, maximum));

  const normalizeDraft = (items, resumeValue) => {
    const resume = resumeEvaluation ? clampAllocation(resumeValue) : 0;
    let remaining = 100 - resume;
    return {
      resume,
      techs: (items || []).map((tech) => {
        const percentage = clampAllocation(tech.percentage, remaining);
        remaining -= percentage;
        return { ...tech, percentage };
      }),
    };
  };

  // Map difficulty to MUI color names (success=green, warning=yellow, error=red)
  const difficultyColor = { Basic: 'success', Intermediate: 'warning', Advanced: 'error' };

  const getTechDraft = useCallback(() => (techs || []).map((tech) => {
      const diffObj = difficulties.find((d) => d.name === tech.name) || {
        difficulty: 'Medium',
      };
      return {
        name: tech.name,
        percentage: tech.percentage,
        difficulty: diffObj.difficulty,
      };
    }), [techs, difficulties]);

  // Initialize/reset the draft from props whenever the editor opens.
  useEffect(() => {
    if (!open) return;

    const draft = normalizeDraft(getTechDraft(), resumeEvaluationPercentage);
    setLocalTechs(draft.techs);
    setResumeEvalPercent(draft.resume);
  }, [open, getTechDraft, resumeEvaluationPercentage]);

  const handleCancel = () => {
    const draft = normalizeDraft(getTechDraft(), resumeEvaluationPercentage);
    setLocalTechs(draft.techs);
    setResumeEvalPercent(draft.resume);
    onClose();
  };

  // Calculate totals
  const techTotal = localTechs.reduce((sum, t) => sum + (parseInt(t.percentage) || 0), 0);
  const totalPercentage = techTotal + (parseInt(resumeEvalPercent) || 0);

  // Handle adding new tech
  const handleAddTech = () => {
    if (!newTechName.trim()) {
      alert('Please enter a technology name');
      return;
    }

    // Check if tech already added
    if (localTechs.some(t => t.name.toLowerCase() === newTechName.trim().toLowerCase())) {
      alert('This technology is already added');
      return;
    }

    const available = 100 - (parseInt(resumeEvalPercent, 10) || 0) - techTotal;
    const percentage = clampAllocation(newTechPercentage, available);
    if (!newTechPercentage || Number.parseInt(newTechPercentage, 10) < 0) {
      alert('Please enter a valid percentage (0-100)');
      return;
    }

    const newTech = {
      name: newTechName.trim(),
      percentage,
      difficulty: newTechDifficulty,
    };

    setLocalTechs([...localTechs, newTech]);
    setNewTechName('');
    setNewTechPercentage('');
    setNewTechDifficulty('Intermediate');
  };

  // Handle percentage change
  const handlePercentageChange = (index, value) => {
    setLocalTechs((current) => {
      const otherTotal = current.reduce((sum, tech, techIndex) => sum + (techIndex === index ? 0 : (parseInt(tech.percentage, 10) || 0)), 0);
      const maximum = 100 - (parseInt(resumeEvalPercent, 10) || 0) - otherTotal;
      return current.map((tech, techIndex) => techIndex === index ? { ...tech, percentage: clampAllocation(value, maximum) } : tech);
    });
  };

  // Handle difficulty change
  const handleDifficultyChange = (index, value) => {
    setLocalTechs((current) =>
      current.map((tech, techIndex) =>
        techIndex === index ? { ...tech, difficulty: value } : tech
      )
    );
  };

  // Handle delete
  const handleDelete = (index) => {
    setLocalTechs((current) => current.filter((_, techIndex) => techIndex !== index));
  };

  // Check if we have techs to save
  const hasTechs = localTechs.length > 0;
  const isValid = !hasTechs || totalPercentage === 100;
  const canSave = isValid;

  return (
    <Dialog className="ttf-dialog ttf-tech-dialog" open={open} onClose={onClose} fullWidth maxWidth="md" scroll="body">
      <DialogTitle><Box className="ttf-tech-dialog-heading"><img className="ttf-heading-icon" src={techStackIcon} alt="" /><Box><Typography component="span" className="ttf-tech-dialog-title">Edit Tech Stack</Typography><Typography variant="body2">Add the technologies and their allocation role.</Typography></Box></Box>
        <IconButton aria-label="Close tech stack editor" onClick={handleCancel} className="ttf-dialog-close"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent className="ttf-tech-dialog-content" dividers sx={{ pb: 0 }}>
        {/* Resume Evaluation Section */}
        {resumeEvaluation && (
          <Card className="ttf-tech-resume-allocation">
            <CardContent>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Resume Evaluation Percentage
              </Typography>
              <TextField
                label="Resume Evaluation %"
                InputLabelProps={{ shrink: true }}
                type="number"
                value={resumeEvalPercent === 0 ? '' : resumeEvalPercent}
                onChange={(e) => {
                  const techTotal = localTechs.reduce((sum, tech) => sum + (parseInt(tech.percentage, 10) || 0), 0);
                  setResumeEvalPercent(clampAllocation(e.target.value, 100 - techTotal));
                }}
                fullWidth
                margin="dense"
                inputProps={{ max: 100, min: 0 }}
                helperText="Percentage of evaluation based on resume"
              />
            </CardContent>
          </Card>
        )}

        {/* Add New Tech Stack Section */}
          <Card className="ttf-tech-add-card">
          <CardContent>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              Add Technology
            </Typography>

            <Box className="ttf-tech-add-grid">
              <TextField
                label="Language Name"
                InputLabelProps={{ shrink: true }}
                type="text"
                value={newTechName}
                onChange={(e) => setNewTechName(e.target.value)}
                size="small"
                placeholder="Language Name"
                fullWidth
              />

              <TextField
                label="Percentage"
                InputLabelProps={{ shrink: true }}
                type="number"
                value={newTechPercentage}
                onChange={(e) => setNewTechPercentage(e.target.value)}
                size="small"
                inputProps={{ min: 0, max: 100 }}
                placeholder="E.g. 30"
              />

              <Box className="ttf-tech-difficulty-controls">
                {['Basic', 'Intermediate', 'Advanced'].map((level) => (
                  <Button
                    key={level}
                    variant={newTechDifficulty === level ? 'contained' : 'outlined'}
                    color={difficultyColor[level]}
                    size="small"
                    sx={{ flex: 1 }}
                    onClick={() => setNewTechDifficulty(level)}
                  >
                    {level}
                  </Button>
                ))}
              </Box>

              <Button
                className="ttf-tech-add-button"
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddTech}
                disabled={!newTechName.trim() || !newTechPercentage}
              >
                Add
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Display Added Tech Stacks */}
        {localTechs.length > 0 ? (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              Current Tech Stack ({localTechs.length})
            </Typography>

            <Grid className="ttf-tech-current-grid" container spacing={2}>
              {localTechs.map((tech, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Paper
                    className="ttf-tech-current-card"
                    sx={{
                      p: 2,
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      backgroundColor: '#fafafa',
                      position: 'relative',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    {/* Tech Name & Delete Button */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {tech.name}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(index)}
                        sx={{
                          color: 'error.main',
                          '&:hover': { backgroundColor: 'error.light' },
                        }}
                      >
                        <RecruitmentAsset name="remove" />
                      </IconButton>
                    </Box>

                    {/* Percentage Input */}
                    <TextField
                      label="Percentage"
                      InputLabelProps={{ shrink: true }}
                      type="number"
                      value={tech.percentage === 0 ? '' : tech.percentage}
                      onChange={(e) => handlePercentageChange(index, e.target.value)}
                      fullWidth
                      size="small"
                      margin="dense"
                      inputProps={{ min: 0, max: 100 }}
                      sx={{ mb: 1 }}
                    />

                    {/* Difficulty Selection */}
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {['Basic', 'Intermediate', 'Advanced'].map((level) => (
                        <Button
                          key={level}
                          variant={tech.difficulty === level ? 'contained' : 'outlined'}
                          color={difficultyColor[level]}
                          size="small"
                          sx={{ flex: 1, fontSize: '0.75rem' }}
                          onClick={() => handleDifficultyChange(index, level)}
                        >
                          {level}
                        </Button>
                      ))}
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Compact allocation summary; percentages remain validated before saving. */}
            <Paper className={`ttf-tech-allocation-summary${totalPercentage !== 100 ? ' is-invalid' : ''}`} sx={{ p: 2, mb: 2, backgroundColor: '#f5f5f5' }}>
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Total Allocation
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: totalPercentage === 100 ? 'success.main' : 'warning.main',
                    }}
                  >
                    {totalPercentage}%
                  </Typography>
                </Box>
                <LinearProgress
                  className="ttf-brand-progress"
                  variant="determinate"
                  value={Math.min(totalPercentage, 100)}
                />
              </Box>

              {/* Breakdown */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1.5 }}>
                {resumeEvaluation && (
                  <Box>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      Resume Evaluation:
                    </Typography>
                    <Chip
                      label={`${resumeEvalPercent}%`}
                      size="small"
                      variant="outlined"
                      sx={{ ml: 0.5 }}
                    />
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    Tech Stack:
                  </Typography>
                  <Chip
                    label={`${techTotal}%`}
                    size="small"
                    variant="outlined"
                    sx={{ ml: 0.5 }}
                  />
                </Box>
              </Box>
            </Paper>
          </Box>
        ) : (
          <Alert className="ttf-tech-empty-notice" severity="info" sx={{ mb: 2 }}>
            No technologies added yet. Add one using the form above.
          </Alert>
        )}

        {/* Validation Message */}
        {hasTechs && totalPercentage !== 100 && (
          <Alert className="ttf-tech-allocation-notice" severity="error" sx={{ mt: 2 }}>
            Total percentage must be exactly 100%. Currently: {totalPercentage}%
          </Alert>
        )}

        {totalPercentage === 100 && hasTechs && (
          <Alert className="ttf-tech-allocation-notice" severity="success" sx={{ mt: 2 }}>
            All percentages add up to 100%. Ready to save!
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={() => {
            if (canSave) {
              onSave(
                localTechs.map((tech) => ({ ...tech })),
                resumeEvalPercent
              );
              onClose();
            }
          }}
          variant="contained"
          disabled={!canSave}
        >
          {canSave ? '✓ Save Changes' : 'Cannot Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TechStackModal;
